import { Injectable } from '@angular/core';
import * as L from 'leaflet'; // ensure this is at top
import 'leaflet-wms-header';
import { createNavigationGlobalIcon } from '../shared/service/icon.service';
import { environment } from 'src/environments/environment';
type AnyLayer = L.Layer

export interface FeedUI {           // the hint text
  showFilterIcon: boolean;   
}

export interface FeedViewModel {
  sections: FeedSection[];
  ui: FeedUI;
}

export interface FeedLegendConfig {
  type?: string;
  color?: any;
  color_legend?: any;
  colorMode?: string;
}

export interface FeedLine {
  text: string;
  subText?: string;
  legendColor?: string;
  iconUrl?: string;
  style?: { [k: string]: string };
}
export interface FeedSection {
  show: boolean;
  className: string;
  style?: { [k: string]: string };
  items: FeedLine[];
}
export interface ClassFilterOption {
  key: string;
  label: string;
  color?: string;
}
export interface BuildFeedSectionsMeta {
  featureCount?: number;
  showCountRow?: boolean;   // <= only override when this is true
}

export interface ParsedCoordinate {
  lat: number;
  lng: number;
}

@Injectable({ providedIn: 'root' })
export class HelperService {

  /**
   * Converts the different class colour-map formats used by feeds into one
   * stable shape.  Consumers should retain `key` for filtering and use
   * `label` only for display.
   */
  getClassFilterOptions(colorMap: Record<string, any> | null | undefined): ClassFilterOption[] {
    if (!colorMap || typeof colorMap !== 'object') {
      return [];
    }

    return Object.entries(colorMap)
      .map(([key, value]): ClassFilterOption => ({
        key,
        label: String(value?.label ?? value ?? key),
        color: typeof value === 'object' ? value?.color : value,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }

  /**
   * Parses the coordinate formats accepted by the map search controls.  The
   * expression is intentionally limited to complete coordinate pairs so that
   * ordinary location searches continue to reach the geocoding provider.
   */
  // parseCoordinatePair(
  //   input: string,
  //   decimalOrder: 'lat-lng' | 'lng-lat' = 'lat-lng'
  // ): ParsedCoordinate | null {
  //   if (!input || typeof input !== 'string') return null;

  //   const value = input
  //     .trim()
  //     .replace(/[º]/g, '°')
  //     .replace(/[′’]/g, "'")
  //     .replace(/[″“”]/g, '"');
  //   const number = '(\\d+(?:\\.\\d+)?)';
  //   const dms = new RegExp(
  //     `^${number}\\s*°\\s*${number}\\s*'\\s*${number}\\s*"\\s*([NS])\\s*,\\s*${number}\\s*°\\s*${number}\\s*'\\s*${number}\\s*"\\s*([EW])$`,
  //     'i'
  //   );
  //   const ddm = new RegExp(
  //     `^${number}\\s*°\\s*${number}\\s*'\\s*([NS])\\s*,\\s*${number}\\s*°\\s*${number}\\s*'\\s*([EW])$`,
  //     'i'
  //   );
  //   const decimalWithHemisphere = new RegExp(
  //     `^${number}\\s*°?\\s*([NS])\\s*,\\s*${number}\\s*°?\\s*([EW])$`,
  //     'i'
  //   );
  //   const decimal = /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

  //   let match = dms.exec(value);
  //   if (match) {
  //     return this.toLatLng(
  //       this.toDecimal(Number(match[1]), Number(match[2]), Number(match[3]), match[4]),
  //       this.toDecimal(Number(match[5]), Number(match[6]), Number(match[7]), match[8])
  //     );
  //   }

  //   match = ddm.exec(value);
  //   if (match) {
  //     return this.toLatLng(
  //       this.toDecimal(Number(match[1]), Number(match[2]), 0, match[3]),
  //       this.toDecimal(Number(match[4]), Number(match[5]), 0, match[6])
  //     );
  //   }

  //   match = decimalWithHemisphere.exec(value);
  //   if (match) {
  //     return this.toLatLng(
  //       this.toDecimal(Number(match[1]), 0, 0, match[2]),
  //       this.toDecimal(Number(match[3]), 0, 0, match[4])
  //     );
  //   }

  //   match = decimal.exec(value);
  //   if (!match) return null;

  //   const first = Number(match[1]);
  //   const second = Number(match[2]);
  //   return decimalOrder === 'lat-lng'
  //     ? this.toLatLng(first, second)
  //     : this.toLatLng(second, first);
  // }

  // private toDecimal(
  //   degrees: number,
  //   minutes: number,
  //   seconds: number,
  //   hemisphere: string
  // ): number | null {
  //   if (minutes < 0 || minutes >= 60 || seconds < 0 || seconds >= 60) {
  //     return null;
  //   }
  //   const decimal = degrees + minutes / 60 + seconds / 3600;
  //   return hemisphere.toUpperCase() === 'S' || hemisphere.toUpperCase() === 'W'
  //     ? -decimal
  //     : decimal;
  // }

  // private toLatLng(lat: number | null, lng: number | null): ParsedCoordinate | null {
  //   if (lat === null || lng === null || !this.validateCoordinates(lng, lat)) {
  //     return null;
  //   }
  //   return { lat, lng };
  // }

  parseCoordinatePair(
    input: string,
    decimalOrder: 'lat-lng' | 'lng-lat' = 'lat-lng'
  ): ParsedCoordinate | null {

    if (!input || typeof input !== 'string') {
      return null;
    }

    const value = input
      .trim()
      .replace(/[º]/g, '°')
      .replace(/[′’]/g, "'")
      .replace(/[″“”]/g, '"');

    const number = '(\\d+(?:\\.\\d+)?)';

    // DMS
    const dms = new RegExp(
      `^${number}\\s*°\\s*${number}\\s*'\\s*${number}\\s*"\\s*([NSWE])\\s*,\\s*${number}\\s*°\\s*${number}\\s*'\\s*${number}\\s*"\\s*([NSWE])$`,
      'i'
    );

    // DDM
    const ddm = new RegExp(
      `^${number}\\s*°\\s*${number}\\s*'\\s*([NSWE])\\s*,\\s*${number}\\s*°\\s*${number}\\s*'\\s*([NSWE])$`,
      'i'
    );

    // Decimal + hemisphere
    const decimalWithHemisphere = new RegExp(
      `^${number}\\s*°?\\s*([NSWE])\\s*,\\s*${number}\\s*°?\\s*([NSWE])$`,
      'i'
    );

    // Existing plain DD
    const decimal =
      /^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/;

    let match = dms.exec(value);

    if (match) {
      const first = this.toDecimal(
        Number(match[1]),
        Number(match[2]),
        Number(match[3]),
        match[4]
      );

      const second = this.toDecimal(
        Number(match[5]),
        Number(match[6]),
        Number(match[7]),
        match[8]
      );

      return this.buildCoordinateFromHemisphere(
        first,
        match[4],
        second,
        match[8]
      );
    }

    match = ddm.exec(value);

    if (match) {
      const first = this.toDecimal(
        Number(match[1]),
        Number(match[2]),
        0,
        match[3]
      );

      const second = this.toDecimal(
        Number(match[4]),
        Number(match[5]),
        0,
        match[6]
      );

      return this.buildCoordinateFromHemisphere(
        first,
        match[3],
        second,
        match[6]
      );
    }

    match = decimalWithHemisphere.exec(value);

    if (match) {
      const first = this.toDecimal(
        Number(match[1]),
        0,
        0,
        match[2]
      );

      const second = this.toDecimal(
        Number(match[3]),
        0,
        0,
        match[4]
      );

      return this.buildCoordinateFromHemisphere(
        first,
        match[2],
        second,
        match[4]
      );
    }

    // Existing DD behaviour
    match = decimal.exec(value);

    if (!match) {
      return null;
    }

    const first = Number(match[1]);
    const second = Number(match[2]);

    return decimalOrder === 'lat-lng'
      ? this.toLatLng(first, second)
      : this.toLatLng(second, first);
  }

  private buildCoordinateFromHemisphere(
    first: number | null,
    firstHemisphere: string,
    second: number | null,
    secondHemisphere: string
  ): ParsedCoordinate | null {

    if (first === null || second === null) {
      return null;
    }

    const firstIsLatitude = /[NS]/i.test(firstHemisphere);
    const secondIsLatitude = /[NS]/i.test(secondHemisphere);

    // Invalid combination, e.g. N followed by S
    // or E followed by W.
    if (firstIsLatitude === secondIsLatitude) {
      return null;
    }

    const lat = firstIsLatitude ? first : second;
    const lng = firstIsLatitude ? second : first;

    return this.toLatLng(lat, lng);
  }

  private toDecimal(
    degrees: number,
    minutes: number,
    seconds: number,
    hemisphere: string
  ): number | null {

    if (
      minutes < 0 ||
      minutes >= 60 ||
      seconds < 0 ||
      seconds >= 60
    ) {
      return null;
    }

    const decimal =
      degrees +
      minutes / 60 +
      seconds / 3600;

    return /[SW]/i.test(hemisphere)
      ? -decimal
      : decimal;
  }

  private toLatLng(
    lat: number | null,
    lng: number | null
  ): ParsedCoordinate | null {

    if (
      lat === null ||
      lng === null ||
      !this.validateCoordinates(lng, lat)
    ) {
      return null;
    }

    return { lat, lng };
  }

  private ensureBoundsPane(map: L.Map, paneName = 'wms-bounds-pane'): string {
    let pane = map.getPane(paneName);
    if (!pane) {
      pane = map.createPane(paneName);
    }

    pane.style.zIndex = '9999';
    pane.style.pointerEvents = 'none';
    return paneName;
  }

  private attachBoundsOverlay(map: L.Map, wmsLayer: L.Layer, rawBounds: any): void {
    if (!rawBounds) {
      return;
    }

    const bounds = L.latLngBounds(rawBounds);
    if (!bounds.isValid()) {
      return;
    }

    const existingOverlay = (wmsLayer as any).__boundsOverlay as L.Rectangle | undefined;
    const existingRemoveHandler = (wmsLayer as any).__boundsOverlayRemoveHandler as
      | (() => void)
      | undefined;

    if (existingRemoveHandler) {
      wmsLayer.off('remove', existingRemoveHandler);
    }

    if (existingOverlay && map.hasLayer(existingOverlay)) {
      map.removeLayer(existingOverlay);
    }

    const boundsOverlay = L.rectangle(bounds, {
      pane: this.ensureBoundsPane(map),
      color: '#ffd400',
      weight: 2,
      fill: false,
      opacity: 1,
      interactive: false,
    });

    boundsOverlay.addTo(map);
    boundsOverlay.bringToFront();

    const removeHandler = () => {
      const overlay = (wmsLayer as any).__boundsOverlay as L.Rectangle | undefined;
      if (overlay && map.hasLayer(overlay)) {
        map.removeLayer(overlay);
      }
    };

    (wmsLayer as any).__boundsOverlay = boundsOverlay;
    (wmsLayer as any).__boundsOverlayRemoveHandler = removeHandler;

    wmsLayer.on('remove', removeHandler);
  }

  private safeAssign(
  label: string,
  value: unknown,
  suffix = '',
  extraVisible?: boolean,     // 👈 new flag
  labelWhenEmpty?: string,     // 👈 optional override label when no value
): string {
  const hasValue = value !== null && value !== undefined && value !== '';
  if (hasValue) {
    return `${label} : ${value}${suffix}`;
  }
  if (extraVisible) {
    return labelWhenEmpty ?? label; // show label even without value
  }
  return '';
}


private pushIf(
    items: FeedLine[],
    label: string,
    value: unknown,
    opts?: 
    { suffix?: string; 
      color?: string, 
      visibleIf?: boolean;   
      labelWhenEmpty?: string; 
      iconUrl?: string;
      style?: { [k: string]: string };
    }
  ) {
    const text = this.safeAssign(label, value, opts?.suffix ?? '',  opts?.visibleIf, opts?.labelWhenEmpty);
    if (text) items.push({ text, legendColor: opts?.color, iconUrl: opts?.iconUrl, style: opts?.style });
  }


getFormattedArea(data: any, metric: string): string {
  if (data == null || data === 'N/A' || data === '') return '';
  const raw = String(data);
  // grab the first number (handles NBSP, commas, etc.)
  const m = raw.match(/[+-]?\d+(?:[.,]\d+)?/);
  if (!m) return '';
  const numberPart = Math.ceil(parseFloat(m[0].replace(/,/g, '')));
  const formatted = numberPart.toLocaleString('en-US');
  return `${formatted} ${metric}`;
}

buildFeedSections(config: FeedLegendConfig, filteredCollection: any, color?: string, filterMode?: { type: string, value: string }, classCounts?: Readonly<Record<string, number>>): FeedViewModel {
    const sections: FeedSection[] = []; {
    const defaultSection: FeedSection = {
      show: false,
      className: 'feed-display feed-class',
      items: [],
    };

    const blackSection: FeedSection = {
      show: false,
      className: 'feed-display oil-class',
      style: { 'background-color': 'black', padding: '7px' },
      items: [],
    };
     const ui: FeedUI = {
      showFilterIcon: false,
    };
    const resolveScalarColor = (fallback: string) => color ?? config?.color ?? fallback;


    switch (config?.type) {
      case 'Tree counting':
        {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Tree crown area', config, filterMode);
        ui.showFilterIcon = false;  
        break;
      }
       case 'Ship': {
         this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Vessel Count', config, filterMode);
         ui.showFilterIcon = true;  
        break;
      }
       case 'Transhipment': {
         this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Transhipment Count', config, filterMode);
         ui.showFilterIcon = true;  
        break;
      }
      case 'Deforestation': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), 'Deforested area', config, filterMode);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Oil': {
       this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Oil spill area", config, filterMode);
       ui.showFilterIcon = false;  
      break;
      }
      case 'Path-SAR': {
        const defaultColor = resolveScalarColor('#ba5a06');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Unpaved road (SAR)", config, filterMode);
        break;
      }
      case 'Plantation-SAR': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Blank spot detection - SAR", config, filterMode);
        break;
      }
      case 'Flood': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Flood extent", config, filterMode);
        ui.showFilterIcon = false;  
        break;
      }
      case 'Path': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor(''), "Path (Unmetalled road)", config, filterMode);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Plantation': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Plantation area", config, filterMode);
        break;
      }
      case 'vessel_prediction': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vessel_prediction", config, filterMode);
        break;
      }
      case 'Vessel-classification': {
        const defaultColor = config.color_legend ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vessel_classification", config, filterMode, classCounts);
        break;
      }
      case 'Vehicle-classification': {
        const defaultColor = config.color_legend ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "vehicle_classification", config, filterMode, classCounts);
        break;
      }
      case 'Road-classification': {
        const defaultColor = config.color ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "road_classification", config, filterMode);
        break;
      }
      case 'Land use/land cover detection': {
        const defaultColor = config.color ?? '#228B22';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Land use/land cover", config, filterMode);
        break;
      }
      case 'Tuna-detection': {
        const defaultColor = resolveScalarColor('#e2e90bff');
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Tuna stock", config, filterMode);
        break;
      }
      case 'Container detection': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('#800080'), 'Container count', config, filterMode);
        ui.showFilterIcon = false;
        break;
      }
      case 'Oil spill type': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('MAGENTA'), 'Oil spill type', config, filterMode);
        ui.showFilterIcon = false;
        break;
      }
      case 'Flood Inundation': {
        this.buildKeyValueSection(defaultSection, filteredCollection, resolveScalarColor('#2E86C1'), 'Flood Inundation', config, filterMode );
        ui.showFilterIcon = false;
        break;
      }
      case 'small-boat': {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '#e2e90bff', 'Small boat count', config, filterMode);
        ui.showFilterIcon = false;
        break;
      }
      case 'Soil Moisture - SAR': {
        const defaultColor = config.color ?? '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Soil Moisture", config, filterMode);
        break;
      }
    }

    if (defaultSection.show) sections.push(defaultSection);
    if (blackSection.show) sections.push(blackSection);

    return { sections, ui };
}
}



private readonly prettyLabel = (k: string) =>
  k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

buildKeyValueSection(
  section: FeedSection,
  filteredCollection: any,
  defaultColor: string,
  emptyFallbackLabel: string,  
  config?: FeedLegendConfig,
  filterMode?: { type: string, value: string },
  classCounts?: Readonly<Record<string, number>>
) {

let entries: [string, any][] = [];

if (filteredCollection && typeof filteredCollection === 'object') {
  entries = Object.entries(filteredCollection);
} else {
  entries = [];
}

  // Generic handler for other CLASS_BASED filters (Soil Moisture, Vessel Classification, Vehicle Classification, Flood Inundation)
  if (config?.colorMode === 'CLASS_BASED' && config?.color && typeof config.color === 'object') {
    const colorMap = config.color;
    const isFloodInundation = config?.type === 'Flood Inundation';
    const areaData = (filteredCollection && typeof filteredCollection === 'object')
      ? (filteredCollection?.area || {}) : {};
    const unit = (filteredCollection && typeof filteredCollection === 'object')
      ? (filteredCollection?.unit || 'sqm') : 'sqm';
    const hasArea = Object.keys(areaData).length > 0;

    // Flood Inundation has an aggregate area row in addition to class entries.
    if (isFloodInundation) {
      const rawArea = filteredCollection?.Inundated_area ?? filteredCollection?.inundated_area;
      if (rawArea != null) {
        const formattedArea = this.getFormattedArea(rawArea, 'sqm');
        section.items.push({ text: `Inundated Area : ${formattedArea}` });
      }
    }

    // Mapping from class label to areaData key
    const labelToAreaKey: Record<string, string> = {
      'Built-up': 'built_up',
      'Roads': 'road',
      'Water': 'water',
      'Open area': 'open_area',
      'Agriculture/fallow land': 'agriculture',
      'Tree/Forest': 'tree/forest',
      'Plantation': 'plantation',
    };

    const classKeys = filterMode?.type === 'classBasedFilter' && filterMode.value
      ? [filterMode.value] : Object.keys(colorMap);
    const isCountFeed = config.type === 'Vessel-classification' || config.type === 'Vehicle-classification';
    if (isCountFeed) {
      // Preserve result summaries as plain rows; classes are rendered once below.
      // Exclude all class aliases, including classes hidden by the current filter.
      const classResultKeys = new Set([
        ...Object.keys(colorMap),
        ...Object.keys(config.color_legend ?? {}),
      ]);
      const totalKey = config.type === 'Vehicle-classification' ? 'vehicle_count' : 'vessel_count';
      for (const [key, value] of entries) {
        if (classResultKeys.has(key)) continue;
        const display = key === totalKey && classCounts !== undefined
          ? Object.values(classCounts).reduce((sum, count) => sum + count, 0)
          : value;
        this.pushIf(section.items, this.prettyLabel(key), display, { visibleIf: true });
      }
    }
    for (const classKey of classKeys) {
      const classInfo = colorMap[classKey];
      if (!classInfo) continue;
      const color = typeof classInfo === 'object' ? classInfo.color : classInfo;
      const label = typeof classInfo === 'object' ? classInfo.label : classInfo;
      let subText: string | undefined;
      if (hasArea && typeof classInfo === 'object' && classInfo.label) {
        const areaKey = labelToAreaKey[classInfo.label];
        const areaValue = areaKey != null ? areaData[areaKey] : undefined;
        if (areaValue != null) {
          subText = `Total Area: ${this.getFormattedArea(areaValue, unit)}`;
        }
      }
      let count: unknown;
      if (isCountFeed) {
        if (classCounts !== undefined) {
          // An explicit filtered count map is authoritative, including no matches.
          count = classCounts[classKey] ?? 0;
        } else {
          const resultKey = Object.keys(config.color_legend ?? {})
            .find(key => config.color_legend[key]?.label === label);
          count = filteredCollection?.[classKey]
            ?? (resultKey !== undefined ? filteredCollection?.[resultKey] : undefined);
        }
      }
      section.items.push({
        text: this.safeAssign(label, count, '', true, label),
        legendColor: color,
        subText,
      });
    }
    section.show = section.items.length > 0;
    return;
  }

  // For Road-classification and Tuna-detection: entries are empty (feed result is null)
  if (
    config?.type === 'Road-classification' ||
    config?.type === 'Tuna-detection'
  ) {
    // Use config.color for legend display
    const colorMap = config.color;
    if (colorMap && typeof colorMap === 'object') {
      for (const classKey of Object.keys(colorMap)) {
        const classInfo = colorMap[classKey];
        this.pushIf(section.items, classInfo.label, '', {
          color: classInfo.color,
          visibleIf: true,
          labelWhenEmpty: classInfo.label,
        });
      }
      section.show = section.items.length > 0;
      return;
    }
  }

  // Empty -> show a single fallback row
if (entries.length === 0 ) {
    this.pushIf(section.items, emptyFallbackLabel, "", {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: emptyFallbackLabel,
    });
    section.show = true;
    return;
}


for (const [key, value] of entries) {
  // 🔹 CASE 1: value is an object with a `metric` -> expand it
  if (value && typeof value === 'object' && 'metric' in value) {
    const metric = value.metric;
    for (const [innerKey, innerValue] of Object.entries(value)) {
      if (innerKey === 'metric') continue; // skip metric itself

      const label = this.prettyLabel(innerKey); // e.g. plantable_area -> Plantable Area
      let display: string;

       if (config?.type === 'Oil' || config?.type === 'Oil spill type') {
        display = this.getFormattedArea(innerValue, metric);
       } else if (config?.type === 'Plantation' || config?.type === 'Plantation-SAR') {
       display = this.roundWithUnit(innerValue, 2, metric);
       } else {
       display = String(value ?? '');
     }
      this.pushIf(section.items, label, display, {
        color: config?.type !== 'Vessel-classification' ? defaultColor : defaultColor[key]?.color_legend,
        visibleIf: true,
        labelWhenEmpty: label,
      });
    }
    continue;
  }

 if (key === 'location_after_one_hour') {
  const canvas = createNavigationGlobalIcon('Yellowgreen',
        'dotted'
      );

 const iconUrl = canvas.toDataURL();
  this.pushIf(
    section.items,
    'Predicted vessel location',
    '',
    {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: 'Predicted vessel location',
      iconUrl: iconUrl
    }
  );

  continue;
 }

  const label = this.prettyLabel(key);
  const isClassBasedLookup =
    config?.type === 'Vessel-classification' || config?.type === 'Vehicle-classification';
  const legendColor = config?.type === 'small-boat' && key === 'sea_state'
    ? undefined
    : isClassBasedLookup ? (defaultColor as any)[key]?.color : defaultColor;
  const lineStyle = config?.type === 'small-boat' && key === 'sea_state'
    ? { 'padding-left': '28px' }
    : undefined;

  this.pushIf(section.items, label, value, {
    color: legendColor,
    visibleIf: true,
    labelWhenEmpty: label,
    style: lineStyle,
  });
}
section.show = section.items.length > 0;
}

roundWithUnit(value: any, decimals: number = 2, metric: string): string {
  let rounded = parseFloat(value).toFixed(decimals);
  return `${rounded} ${metric}`;
}

public buildTooltipContent(
  props: Record<string, unknown> = {},
  rawLen?: unknown,
  centroidCoordinates?: [number, number] | number[] | string
): string {
  const esc = (v: any) =>
    String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loc =
    Array.isArray(centroidCoordinates)
      ? centroidCoordinates.join(', ')       // original precision, lon,lat order
      : (centroidCoordinates ?? '');
  const locParts = String(loc).split(',').map(p => p.trim());

  const conf = props?.['confidence'];        // bracket access fixes index-signature warning
  const confidenceLine = conf != null ? `<b>Confidence:</b> ${esc(conf)}<br>` : '';
  const lengthLine     = rawLen ? `<b>Vessel length:</b> ${esc(rawLen)}<br>` : '';
  const lng   = loc !== '' ? `<b>Lng:</b> ${esc(locParts[0])}<br>` : '';
  const lat   = loc !== '' ? `<b>Lat:</b> ${esc(locParts[1])}<br>` : '';

  return `
    <div style="background-color:black;color:white;padding:5px;max-width:350px;">
      ${confidenceLine}${lengthLine}${lng}${lat}
    </div>
  `;
}

public computeAnchorAndLocation(
    feature: any,
    latlng: L.LatLng
  ): { anchor: L.LatLng; centroidCoordinates: [number, number] } {
    const t = (window as any).turf;
    // defaults: mouse position if nothing else works
    let centroidCoordinates: [number, number] = [latlng.lng, latlng.lat]; // [lon, lat]
    let anchor: L.LatLng = latlng;

    try {
      if (feature?.geometry?.type === 'Point') {
        const coords = feature.geometry.coordinates as [number, number];
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      } else if (t?.pointOnFeature) {
        const pt = t.pointOnFeature(feature);
        const coords = pt?.geometry?.coordinates as [number, number] | undefined;
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      }
      // keep anchor aligned with what we display (GeoJSON is [lon, lat])
      anchor = L.latLng(centroidCoordinates[1], centroidCoordinates[0]);
    } catch {
      // fall back already set
    }
    return { anchor, centroidCoordinates };
}

normalizeConfig(input: any) {
  const src = input?.config ?? input ?? {};
  const { type, filterKey, class: cls, color } = src;
  return { type, filterKey, class: cls, color };
}

classCqlStrict(cls: unknown, field = 'class'): string {
  const f = `"${field}"`;
  const s = String(cls ?? '').trim().toLowerCase();
  if (!s) return '';

  if (s === 'ship') {
    return `strToLowerCase(${f}) = 'ship'`;
  }
  if (s === 'transhipment' || s === 'transshipment') {
    // accept both spellings present in datasets
    return `strToLowerCase(${f}) IN ('transhipment','transshipment')`;
  }
  return ''; // do NOT coerce anything else into transhipment
}

getFeedResultKeyForClass(feedType?: string): 'vessel_count' | 'transhipment_count' {
  const s = String(feedType ?? '').trim().toLowerCase();
  return s.includes('trans') ? 'transhipment_count' : 'vessel_count';
}

combineFilters(...parts: Array<string | undefined | null>): string {
  const wrapIfOr = (s: string) =>
    /\bOR\b/i.test(s) && !(s.trim().startsWith('(') && s.trim().endsWith(')'))
      ? `(${s.trim()})` : s.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const s = (p ?? '').trim();
    if (!s) continue;
    const norm = s.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(wrapIfOr(s));
  }
  return out.join(' AND ');
}

makeWfsParams(layerName: string){
 return new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeName: layerName,                 
    outputFormat: 'application/json',
    srsName: 'EPSG:4326'
  });
}
  validateCoordinates(longitude, latitude) {
    // Check if values are numbers
    if (isNaN(longitude) || isNaN(latitude)) {
      return false;
    }
    
    // Check longitude range (-180 to 180)
    if (longitude < -180 || longitude > 180) {
      return false;
    }
    
    // Check latitude range (-90 to 90)
    if (latitude < -90 || latitude > 90) {
      return false;
    }
    
    return true;
  }

  createWmsLayer(
  map: L.Map,
  urlData: any,
  shapeFile: string,
  zIndex: number = 1000,
  isValueAddedProduct: boolean = false,
  baseUrlValue?: string,
  pane?: string,  
  bandValue?: any
): AnyLayer   | null {
  if (!urlData?.layer && !isValueAddedProduct) return null;
  const layerObj = isValueAddedProduct ? urlData : urlData.layer;
  const bounds = layerObj.bounds;
  const baseUrl = environment.geoServerUrl;
  let layerName = layerObj.layerName ? layerObj.layerName : layerObj.layer;
  let bands = bandValue ? bandValue : layerObj.bands;
  let style = layerObj.tiffStyle;
  let algorithm = layerObj.algorithm;

  // Handle shapes overlay if shapeFile is not "image"
  if (shapeFile !== 'image' && Array.isArray(layerObj.shapes) && layerObj.shapes.length > 0) {
    const shapeNames = layerObj.shapes.map((s: any) => s.layerName).join(',');
    const shapeStyles = layerObj.shapes.map((s: any) => s.style).filter(Boolean).join(',');

    layerName += ',' + shapeNames; 
    if (shapeStyles) style += ',' + shapeStyles;
  }

  // Basic WMS params
  let params: any = {
    layers: layerName,
    format: 'image/png',
    transparent: true,
    maxZoom: 23,
    zIndex: zIndex,
    pane: pane || 'tilePane'
  };

  if (style) params.styles = style;

  let env = '';
 
  // Handle env (band min/max or multi-band)
  if (bands?.length === 1) {
    const { min, max } = bands[0];
    env += `minValue:${min};maxValue:${max};`;
  } else if (bands?.length > 1) {
    env += bands
      .map((b: any, i: number) => {
        let e = `b${i + 1}:${b.band};`;
        if (b.min != null && b.max != null) {
          e += `b${i + 1}minValue:${b.min};b${i + 1}maxValue:${b.max};`;
         }
        return e;
      })
      .join('');
    // params.env = env;
  }
  if (algorithm) {
    env += `algo:${algorithm};`;
  }
 
  if (env) {
    params.env = env;
  }
  // Create & add WMS layer
  const wmsLayer = this.createWmsLayerWithHeader(map, params);
  wmsLayer.addTo(map);
  this.attachBoundsOverlay(map, wmsLayer, bounds);

  if (bounds) {
    const leafletBounds = L.latLngBounds(bounds);
    if (leafletBounds.isValid()) {
      map.fitBounds(leafletBounds);
    }
  }
  return wmsLayer;
}
createWmsLayerWithHeader(
  map: L.Map,
  params: any
): L.TileLayer {
  const storedJwtToken = sessionStorage.getItem('jwttoken') || '';
  const sanitizedJwtToken = storedJwtToken.replace(/^"|"$/g, '');

  const headers = [
    { header: 'Authorization', value: `Bearer ${sanitizedJwtToken}` },
  ];

  const wmsHeaderFactory =
    (L as any).TileLayer?.wmsHeader || (L as any).tileLayer?.wmsHeader;

  if (!wmsHeaderFactory) {
    throw new Error('leaflet-wms-header plugin not loaded. Cannot send headers.');
  }

  const layer = wmsHeaderFactory(environment.geoServerUrl, params, headers, null) as L.TileLayer;
  return layer;
}

}


